import { useEffect, useState } from 'react';
import { track } from '@vercel/analytics';
import { usePDIContext } from '../context/PDIContext';
import { PAYMENTS_ENABLED, UNLOCK_PRICE_RUPEES, saveUnlock } from '../utils/unlock';
import {
  X,
  Lock,
  FileDown,
  Printer,
  MessageSquare,
  ShieldCheck,
  Loader2,
  Sparkles,
  CheckCircle2,
  Info,
  Link2,
} from 'lucide-react';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load payment library'));
    document.body.appendChild(script);
  });

const BENEFITS = [
  { icon: FileDown, text: 'Professional PDF report with your photos, notes & PDI score' },
  { icon: MessageSquare, text: 'Dealer negotiation scripts for every failed item' },
  { icon: Printer, text: 'Printable checklists for offline use at the dealership' },
  { icon: Link2, text: 'Shareable web link to your report — perfect for WhatsApp' },
  { icon: ShieldCheck, text: 'One-time payment — no subscription, no login' },
];

// feature: analytics label for where the paywall was hit ('report', 'printable', 'dealer-scripts')
const PaywallModal = ({ feature, onClose, onUnlocked }) => {
  const { pdiScore, carProfile } = usePDIContext();
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Preview only makes sense once there's a report to preview.
  const showPreview = (pdiScore?.checked || 0) > 0;
  const carName = [carProfile?.carMake, carProfile?.carModel].filter(Boolean).join(' ');
  const scoreColor =
    pdiScore?.scorePercentage >= 90
      ? 'text-brand-600'
      : pdiScore?.scorePercentage >= 70
        ? 'text-accent-600'
        : 'text-danger-600';

  // Defense in depth: every call site already checks isUnlocked() (which is
  // forced true while payments are disabled) before rendering this modal,
  // so this only fires if something skips that check.
  useEffect(() => {
    if (!PAYMENTS_ENABLED) {
      onUnlocked();
      return;
    }
    track('hit_paywall', { feature });
  }, [feature]);

  if (!PAYMENTS_ENABLED) return null;

  const handlePay = async () => {
    setIsPaying(true);
    setError('');

    try {
      await loadRazorpayScript();

      const orderRes = await fetch('/api/create-order', { method: 'POST' });
      if (!orderRes.ok) {
        throw new Error(
          orderRes.status === 503
            ? 'Payments are not set up yet. Please try again later.'
            : 'Could not start the payment. Please try again.'
        );
      }
      const { orderId, amount, currency, keyId } = await orderRes.json();

      const razorpay = new window.Razorpay({
        key: keyId,
        order_id: orderId,
        amount,
        currency,
        name: 'Car PDI',
        description: 'Full report & dealer scripts unlock',
        theme: { color: '#0f172a' },
        handler: async (response) => {
          try {
            const verifyRes = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(response),
            });
            const result = await verifyRes.json();
            if (!verifyRes.ok || !result.verified) {
              throw new Error('verification failed');
            }
            saveUnlock({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
            });
            track('payment_success', { feature });
            // Show the "unlocked on this device" confirmation before
            // proceeding, so users know to save their report now.
            setDone(true);
          } catch {
            setError(
              'Payment received but verification failed. If money was deducted, it will be refunded automatically.'
            );
          } finally {
            setIsPaying(false);
          }
        },
        modal: {
          ondismiss: () => setIsPaying(false),
        },
      });
      razorpay.on('payment.failed', () => {
        setError('Payment failed. No money was deducted - please try again.');
        setIsPaying(false);
      });
      razorpay.open();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setIsPaying(false);
    }
  };

  if (done) {
    return (
      <div className="modal-backdrop flex items-end sm:items-center justify-center p-4 z-50">
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 to-teal-500 flex items-center justify-center mx-auto shadow-lg">
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h3 className="font-display text-2xl font-bold text-slate-800 mt-5">
              You're unlocked!
            </h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Your PDF report, shareable link, printable checklists and dealer
              scripts are now available.
            </p>

            <div className="mt-5 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                This unlock is saved to <span className="font-bold">this device only</span>.
                If you clear your browser data or switch to another phone or browser,
                you'll need to unlock again — so it's best to <span className="font-bold">download your report now</span> while it's open.
              </p>
            </div>

            <button
              type="button"
              onClick={onUnlocked}
              className="mt-5 w-full gradient-animated rounded-2xl flex items-center justify-center gap-2 py-4 px-5 text-white font-bold text-lg shadow-lg btn-touch"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop flex items-end sm:items-center justify-center p-4 z-50">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="relative overflow-hidden bg-slate-900 p-5 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 via-transparent to-teal-500/20" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-brand-300 tracking-wide flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                ONE-TIME UNLOCK
              </p>
              <h3 className="font-display text-2xl font-bold mt-1">
                Get your PDI report
              </h3>
              <p className="text-sm text-slate-300 mt-1">
                Your inspection is free. Unlock the report & dealer tools for
                a one-time <span className="font-bold text-white">₹{UNLOCK_PRICE_RUPEES}</span>.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center btn-touch flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview of the user's real report (blurred to imply more) */}
        {showPreview && (
          <div className="px-5 pt-5">
            <div className="relative rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-inner">
              {/* Sharp mini report cover — real data */}
              <div className="relative bg-slate-900 px-4 py-3 text-white">
                <p className="text-[10px] font-bold text-brand-300 tracking-wide">CAR PDI REPORT</p>
                <div className="flex items-end justify-between mt-1">
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{carName || 'Your car'}</p>
                    <p className="text-[10px] text-slate-400">
                      {pdiScore.checked} of {pdiScore.total} items checked
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold leading-none">
                    {pdiScore.scorePercentage}
                    <span className="text-sm">%</span>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 divide-x divide-slate-100 bg-white">
                <div className="p-2 text-center">
                  <p className="text-lg font-bold text-brand-600">{pdiScore.pass}</p>
                  <p className="text-[9px] text-slate-400 font-semibold">PASS</p>
                </div>
                <div className="p-2 text-center">
                  <p className="text-lg font-bold text-danger-600">{pdiScore.fail}</p>
                  <p className="text-[9px] text-slate-400 font-semibold">FAIL</p>
                </div>
                <div className="p-2 text-center">
                  <p className="text-lg font-bold text-accent-600">{pdiScore.attention}</p>
                  <p className="text-[9px] text-slate-400 font-semibold">ATTENTION</p>
                </div>
              </div>
              {/* Blurred faux content implying more pages */}
              <div className="px-4 py-3 space-y-2 select-none" aria-hidden="true" style={{ filter: 'blur(4px)' }}>
                <div className="h-2.5 rounded bg-slate-200 w-3/4" />
                <div className="h-2.5 rounded bg-slate-200 w-full" />
                <div className="h-2.5 rounded bg-slate-200 w-5/6" />
                <div className="h-2.5 rounded bg-slate-100 w-2/3" />
              </div>
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
            </div>
            <p className="text-[11px] text-slate-400 text-center mt-2">
              This is your report — unlock to download the full PDF
            </p>
          </div>
        )}

        {/* Benefits */}
        <div className="p-5">
          <ul className="space-y-3">
            {BENEFITS.map(({ icon: Icon, text }, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-brand-600" />
                </div>
                <span className="text-sm text-slate-700 leading-snug">{text}</span>
              </li>
            ))}
          </ul>

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-danger-50 border border-danger-200 text-sm text-danger-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handlePay}
            disabled={isPaying}
            className="mt-5 w-full gradient-animated rounded-2xl flex items-center justify-center gap-2 py-4 px-5 text-white font-bold text-lg shadow-lg btn-touch disabled:opacity-70"
          >
            {isPaying ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Lock className="w-5 h-5" />
            )}
            {isPaying ? 'Opening payment...' : `Unlock for ₹${UNLOCK_PRICE_RUPEES}`}
          </button>

          <p className="mt-3 text-[11px] text-slate-400 text-center leading-relaxed">
            Pay via UPI, card or netbanking (secured by Razorpay). The checklist
            itself stays free - a basic text summary is always available.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
